import { Navigate, Route, Routes } from "react-router";
import { RouteTransition } from "@/Components/RouteTransition";
import { CalendarRoute } from "./Calendar/CalendarRoute";
import { canAccessClientBilling } from "@/utils/billing-access";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import useAccountInfo from "@/hooks/useAccountInfo";
import { useAnalyticsSurface } from "@/hooks/useAnalyticsSurface";
import { useClientFeature } from "@/hooks/useClientFeature";
import { useDashboardSurfaces } from "@/hooks/useDashboardSurfaces";
import { useIntegrationAccess } from "@/hooks/useIntegrationAccess";
import { useShallow } from "zustand/react/shallow";
import { lazy, Suspense, type ReactElement } from "react";
import { Spinner } from "@heroui/react";
import { canViewChatBooking } from "./ChatBooking/chatBookingAccess";

const TaxRegisterSetup = lazy(() =>
  import("./TaxRegister/Setup/Setup").then((module) => ({
    default: module.TaxRegisterSetup,
  })),
);
const TaxRegisterSetupBasicInfo = lazy(() =>
  import("./TaxRegister/Setup/BasicInfo").then((module) => ({
    default: module.TaxRegisterSetupBasicInfo,
  })),
);
const TaxRegisterSetupCertificate = lazy(() =>
  import("./TaxRegister/Setup/Certificate").then((module) => ({
    default: module.TaxRegisterSetupCertificate,
  })),
);
const TaxRegisterSetupBusinessPremise = lazy(() =>
  import("./TaxRegister/Setup/BusinessPremise/BusinessPremise").then(
    (module) => ({
      default: module.TaxRegisterSetupBusinessPremise,
    }),
  ),
);
const TaxRegisterSetupElectronicDevice = lazy(() =>
  import("./TaxRegister/Setup/ElectronicDevice/ElectronicDevice").then(
    (module) => ({
      default: module.TaxRegisterSetupElectronicDevice,
    }),
  ),
);
const TaxRegisterSetupAct = lazy(() =>
  import("./TaxRegister/Setup/Act").then((module) => ({
    default: module.TaxRegisterSetupAct,
  })),
);
const TaxRegisterSetupPrinters = lazy(() =>
  import("./TaxRegister/Setup/Printers").then((module) => ({
    default: module.TaxRegisterSetupPrinters,
  })),
);
const TaxRegisterSetupReceiptPrinter = lazy(() =>
  import("./TaxRegister/Setup/ReceiptPrinter").then((module) => ({
    default: module.TaxRegisterSetupReceiptPrinter,
  })),
);
const TaxRegisterSetupCashRegisterSettings = lazy(() =>
  import("./TaxRegister/Setup/CashRegisterSettings").then((module) => ({
    default: module.TaxRegisterSetupCashRegisterSettings,
  })),
);
const TaxRegisterSetupAutomaticInvoiceIssuing = lazy(() =>
  import("./TaxRegister/Setup/AutomaticInvoiceIssuing").then((module) => ({
    default: module.TaxRegisterSetupAutomaticInvoiceIssuing,
  })),
);
const TaxRegisterReceiptSettings = lazy(() =>
  import("./TaxRegister/Setup/ReceiptSettings").then((module) => ({
    default: module.TaxRegisterReceiptSettings,
  })),
);
const TaxRegisterSetupAccountingSettings = lazy(() =>
  import("./TaxRegister/Setup/AccountingSettings").then((module) => ({
    default: module.TaxRegisterSetupAccountingSettings,
  })),
);
const TaxRegisterDocumentSettings = lazy(() =>
  import("./TaxRegister/Setup/DocumentSettings").then((module) => ({
    default: module.TaxRegisterDocumentSettings,
  })),
);
const TaxRegisterSetupEmailAddress = lazy(() =>
  import("./TaxRegister/Setup/EmailAddress").then((module) => ({
    default: module.TaxRegisterSetupEmailAddress,
  })),
);
const TaxRegisterSetupPOSSettings = lazy(() =>
  import("./TaxRegister/Setup/POSSettings").then((module) => ({
    default: module.TaxRegisterPOSSettings,
  })),
);
const TaxRegisterInvoices = lazy(() =>
  import("./TaxRegister/Invoices/Invoices").then((module) => ({
    default: module.TaxRegisterInvoices,
  })),
);
const LimeAdmin = lazy(() =>
  import("./LimeAdmin/LimeAdmin").then((module) => ({
    default: module.LimeAdmin,
  })),
);
const POS = lazy(() =>
  import("./TaxRegister/Invoices/POS/POS").then((module) => ({
    default: module.POS,
  })),
);
const CashRegister = lazy(() =>
  import("./TaxRegister/CashRegister/CashRegister").then((module) => ({
    default: module.CashRegister,
  })),
);
const CashRegisterStatus = lazy(() =>
  import("./TaxRegister/CashRegister/Status").then((module) => ({
    default: module.CashRegisterStatus,
  })),
);
const CashRegisterClosure = lazy(() =>
  import("./TaxRegister/CashRegister/Closure").then((module) => ({
    default: module.CashRegisterClosure,
  })),
);
const ClientSettings = lazy(() =>
  import("./ClientSettings/ClientSettings").then((module) => ({
    default: module.ClientSettings,
  })),
);
const Users = lazy(() => import("./Users/Users"));
const Waitlist = lazy(() =>
  import("./Waitlist/Waitlist").then((module) => ({
    default: module.Waitlist,
  })),
);
const ChatBooking = lazy(() =>
  import("./ChatBooking/ChatBooking").then((module) => ({
    default: module.ChatBooking,
  })),
);
const FixedAppointments = lazy(() =>
  import("./FixedAppointments/FixedAppointments").then((module) => ({
    default: module.FixedAppointments,
  })),
);
const AnalyticsV2 = lazy(() =>
  import("./AnalyticsV2/AnalyticsV2").then((module) => ({
    default: module.AnalyticsV2,
  })),
);
const Analytics = lazy(() =>
  import("./Analytics/Analytics").then((module) => ({
    default: module.Analytics,
  })),
);
const Billing = lazy(() =>
  import("./Billing/Billing").then((module) => ({
    default: module.Billing,
  })),
);
const Community = lazy(() =>
  import("./Community/Community").then((module) => ({
    default: module.Community,
  })),
);
const Locations = lazy(() =>
  import("./Locations/Locations").then((module) => ({
    default: module.Locations,
  })),
);
const MarketplaceSettings = lazy(() =>
  import("./Marketplace/MarketplaceOverview").then((module) => ({
    default: module.MarketplaceOverview,
  })),
);
const MarketplaceSetup = lazy(() =>
  import("./Marketplace/OnboardingFlow/MarketplaceSetup").then((module) => ({
    default: module.MarketplaceSetup,
  })),
);
const MarketplaceLocationSetup = lazy(() =>
  import("./Marketplace/OnboardingFlow/LocationSetup").then((module) => ({
    default: module.LocationSetup,
  })),
);
const MarketplaceLocationProfile = lazy(() =>
  import("./Marketplace/OnboardingFlow/LocationProfile").then((module) => ({
    default: module.LocationProfile,
  })),
);
const MarketplaceImageGuidelinesStep = lazy(() =>
  import("./Marketplace/OnboardingFlow/steps/ImageGuidelinesStep").then(
    (module) => ({
      default: module.ImageGuidelinesStep,
    }),
  ),
);
const MarketplaceImageUploadStep = lazy(() =>
  import("./Marketplace/OnboardingFlow/steps/ImageUploadStep").then(
    (module) => ({
      default: module.ImageUploadStep,
    }),
  ),
);
const MarketplaceLocationPickerStep = lazy(() =>
  import("./Marketplace/OnboardingFlow/steps/LocationPickerStep").then(
    (module) => ({
      default: module.LocationPickerStep,
    }),
  ),
);
const MarketplaceDescriptionStep = lazy(() =>
  import("./Marketplace/OnboardingFlow/steps/DescriptionStep").then(
    (module) => ({
      default: module.DescriptionStep,
    }),
  ),
);
const MarketplaceDetailsStep = lazy(() =>
  import("./Marketplace/OnboardingFlow/steps/ExtraDetailsStep").then(
    (module) => ({
      default: module.ExtraDetailsStep,
    }),
  ),
);
const MarketplaceConfigStep = lazy(() =>
  import("./Marketplace/OnboardingFlow/steps/MarketplaceConfigStep").then(
    (module) => ({
      default: module.MarketplaceConfigStep,
    }),
  ),
);
const Customers = lazy(() => import("./Customers/Customers"));
const CustomerPage = lazy(() =>
  import("./Customers/CustomerPage/Customer").then((module) => ({
    default: module.CustomerPage,
  })),
);
const Schedule = lazy(() =>
  import("./Schedule/Schedule").then((module) => ({
    default: module.Schedule,
  })),
);
const NotificationSettings = lazy(() =>
  import("./NotificationSettings/NotificationSettings").then((module) => ({
    default: module.NotificationSettings,
  })),
);
const ManageNotificationSetting = lazy(() =>
  import("./NotificationSettings/ManageNotificationSetting").then((module) => ({
    default: module.ManageNotificationSetting,
  })),
);
const ServicesPage = lazy(() =>
  import("./Services/ServicesPage").then((module) => ({
    default: module.ServicesPage,
  })),
);
const Services = lazy(() =>
  import("./Services/Services").then((module) => ({
    default: module.Services,
  })),
);
const ServiceCategories = lazy(() =>
  import("./Services/ServiceCategories").then((module) => ({
    default: module.ServiceCategories,
  })),
);
const ManageCategory = lazy(() =>
  import("./Services/ManageCategory").then((module) => ({
    default: module.ManageCategory,
  })),
);
const ManageService = lazy(() =>
  import("./Services/ManageService").then((module) => ({
    default: module.ManageService,
  })),
);
const ServiceDiscounts = lazy(() =>
  import("./Services/ServiceDiscounts").then((module) => ({
    default: module.ServiceDiscounts,
  })),
);
const AnchorReview = lazy(() =>
  import("./Services/AnchorReview/AnchorReview").then((module) => ({
    default: module.AnchorReview,
  })),
);
const ManageServiceDiscount = lazy(() =>
  import("./Services/ManageServiceDiscount").then((module) => ({
    default: module.ManageServiceDiscount,
  })),
);
const ChangeLog = lazy(() =>
  import("./ChangeLog/ChangeLog").then((module) => ({
    default: module.default,
  })),
);
const ServiceAddons = lazy(() =>
  import("./Services/ServiceAddons").then((module) => ({
    default: module.ServiceAddons,
  })),
);
const ServiceAddonCategories = lazy(() =>
  import("./Services/ServiceAddonCategories").then((module) => ({
    default: module.ServiceAddonCategories,
  })),
);
const ManageServiceAddon = lazy(() =>
  import("./Services/ManageServiceAddon").then((module) => ({
    default: module.ManageServiceAddon,
  })),
);
const ManageServiceAddonCategory = lazy(() =>
  import("./Services/ManageServiceAddonCategory").then((module) => ({
    default: module.ManageServiceAddonCategory,
  })),
);
const DiscountCodes = lazy(() =>
  import("./DiscountCodes/DiscountCodes").then((module) => ({
    default: module.DiscountCodes,
  })),
);
const ManageDiscountCode = lazy(() =>
  import("./DiscountCodes/ManageDiscountCode").then((module) => ({
    default: module.ManageDiscountCode,
  })),
);
const Stripe = lazy(() =>
  import("./StripeSettings/Stripe").then((module) => ({
    default: module.Stripe,
  })),
);
const StripeGeneralTab = lazy(() =>
  import("./StripeSettings/GeneralTab").then((module) => ({
    default: module.GeneralTab,
  })),
);
const StripePaymentsOverviewTab = lazy(() =>
  import("./StripeSettings/PaymentsOverviewTab").then((module) => ({
    default: module.PaymentsOverviewTab,
  })),
);
const StripeAccountSettings = lazy(() =>
  import("./StripeSettings/AccountSettings").then((module) => ({
    default: module.AccountSettings,
  })),
);
const StripeServicePaymentSettings = lazy(() =>
  import("./StripeSettings/ServicePaymentSettings").then((module) => ({
    default: module.ServicePaymentSettings,
  })),
);
const StripeStatus = lazy(() =>
  import("./StripeSettings/Status").then((module) => ({
    default: module.StripeStatus,
  })),
);
const Integrations = lazy(() =>
  import("./Integrations/Integrations").then((module) => ({
    default: module.Integrations,
  })),
);
const IntegrationsOverview = lazy(() =>
  import("./Integrations/IntegrationsOverview").then((module) => ({
    default: module.IntegrationsOverview,
  })),
);
const FacebookConversionApi = lazy(() =>
  import("./Integrations/FacebookConversionApi").then((module) => ({
    default: module.FacebookConversionApi,
  })),
);
const ApiKeys = lazy(() =>
  import("./Integrations/ApiKeys/ApiKeys").then((module) => ({
    default: module.default,
  })),
);
const Webhooks = lazy(() =>
  import("./Integrations/Webhooks/Webhooks").then((module) => ({
    default: module.Webhooks,
  })),
);
const ManageWebhookEndpoint = lazy(() =>
  import("./Integrations/Webhooks/ManageWebhookEndpoint").then((module) => ({
    default: module.ManageWebhookEndpoint,
  })),
);
const PaymentTerminals = lazy(() =>
  import("./Integrations/PaymentTerminals/PaymentTerminals").then((module) => ({
    default: module.PaymentTerminals,
  })),
);
const SumUpOAuthCallback = lazy(() =>
  import("./Integrations/PaymentTerminals/SumUpOAuthCallback").then(
    (module) => ({ default: module.SumUpOAuthCallback }),
  ),
);
const ManageApiKey = lazy(() =>
  import("./Integrations/ApiKeys/ManageApiKey").then((module) => ({
    default: module.default,
  })),
);
const GiftCard = lazy(() =>
  import("./GiftCard/GiftCard").then((module) => ({
    default: module.GiftCard,
  })),
);
const GiftCardDetails = lazy(() =>
  import("./GiftCard/GiftCardDetails").then((module) => ({
    default: module.GiftCardDetails,
  })),
);
const Subscriptions = lazy(() =>
  import("./Subscriptions/Subscriptions").then((module) => ({
    default: module.Subscriptions,
  })),
);
const SubscriptionsTab = lazy(() =>
  import("./Subscriptions/SubscriptionsTab").then((module) => ({
    default: module.SubscriptionsTab,
  })),
);
const TagsTab = lazy(() =>
  import("./Subscriptions/components/tabs").then((module) => ({
    default: module.TagsTab,
  })),
);
const CategoriesTab = lazy(() =>
  import("./Subscriptions/components/tabs").then((module) => ({
    default: module.CategoriesTab,
  })),
);
const SubscriptionSettingsTab = lazy(() =>
  import("./Subscriptions/components/SubscriptionSettingsTab").then(
    (module) => ({
      default: module.SubscriptionSettingsTab,
    }),
  ),
);
const SubscriptionVariations = lazy(() =>
  import("./Subscriptions/SubscriptionVariations").then((module) => ({
    default: module.SubscriptionVariations,
  })),
);
const CustomerSubscriptionsOverviewLayout = lazy(() =>
  import(
    "./CustomerSubscriptionOverview/CustomerSubscriptionsOverviewLayout"
  ).then((module) => ({
    default: module.CustomerSubscriptionsOverviewLayout,
  })),
);
const FreezeRequestsTab = lazy(() =>
  import("./CustomerSubscriptionOverview/FreezeRequestsTab").then((module) => ({
    default: module.FreezeRequestsTab,
  })),
);
const CustomerSubscriptionsOverview = lazy(() =>
  import("./CustomerSubscriptionOverview/CustomerSubscriptionsOverview").then(
    (module) => ({
      default: module.default,
    }),
  ),
);
const ResourceGroups = lazy(() =>
  import("./ResourceGroups/ResourceGroups").then((module) => ({
    default: module.ResourceGroups,
  })),
);
const ManageResourceGroup = lazy(() =>
  import("./ResourceGroups/ManageResourceGroup").then((module) => ({
    default: module.ManageResourceGroup,
  })),
);
const ManageResource = lazy(() =>
  import("./ResourceGroups/ManageResource").then((module) => ({
    default: module.ManageResource,
  })),
);
const ResourceGroupGeneralTab = lazy(() =>
  import("./ResourceGroups/Tabs/GeneralTab").then((module) => ({
    default: module.GeneralTab,
  })),
);
const ResourceGroupResourcesTab = lazy(() =>
  import("./ResourceGroups/Tabs/ResourcesTab").then((module) => ({
    default: module.ResourcesTab,
  })),
);
const ResourceGroupScheduleTab = lazy(() =>
  import("./ResourceGroups/Tabs/ScheduleTab").then((module) => ({
    default: module.ScheduleTab,
  })),
);
const ResourceGroupServicesTab = lazy(() =>
  import("./ResourceGroups/Tabs/ServiceTab").then((module) => ({
    default: module.ServicesTab,
  })),
);
const Products = lazy(() =>
  import("./Products/Products").then((module) => ({
    default: module.Products,
  })),
);
const ManageProduct = lazy(() =>
  import("./Products/ManageProduct").then((module) => ({
    default: module.ManageProduct,
  })),
);
const ManageProductCategory = lazy(() =>
  import("./Products/ManageProductCategory").then((module) => ({
    default: module.ManageProductCategory,
  })),
);
const DeliveryOrders = lazy(() =>
  import("./DeliveryOrders/DeliveryOrders").then((module) => ({
    default: module.DeliveryOrders,
  })),
);
const ManageDeliveryOrder = lazy(() =>
  import("./DeliveryOrders/ManageDeliveryOrder/ManageDeliveryOrder").then(
    (module) => ({
      default: module.ManageDeliveryOrder,
    }),
  ),
);
const ProductStock = lazy(() =>
  import("./ProductStock/ProductStock").then((module) => ({
    default: module.ProductStock,
  })),
);
const StockTaking = lazy(() =>
  import("./StockTaking/StockTaking").then((module) => ({
    default: module.StockTaking,
  })),
);
const StockTakingSession = lazy(() =>
  import("./StockTaking/StockTakingSession").then((module) => ({
    default: module.default,
  })),
);
const CreateProductInternalUse = lazy(() =>
  import("./ProductInternalUse/CreateProductInternalUse").then((module) => ({
    default: module.CreateProductInternalUse,
  })),
);
const CreateProductWriteOff = lazy(() =>
  import("./ProductWriteOff/CreateProductWriteOff").then((module) => ({
    default: module.CreateProductWriteOff,
  })),
);

const defaultLazyFallback = (
  <div className="text-default-500 p-6 text-center text-sm">
    <Spinner />
  </div>
);

const withSuspense = (element: ReactElement) => (
  <Suspense fallback={defaultLazyFallback}>{element}</Suspense>
);

export const DashboardRoutes = () => {
  const { isFeatureEnabled } = useClientFeature();

  const analyticsSurface = useAnalyticsSurface();

  const dashboardSurfaces = useDashboardSurfaces();

  const { canAccess: canAccessIntegration, hasAnyIntegration } =
    useIntegrationAccess();

  const accountInfo = useAccountInfo(useShallow((state) => state.accountInfo));

  const permissions = useUserPermissionsStore(
    useShallow((state) => state.permissions),
  );

  return (
    <div
      className={
        // overflow-x-clip (not hidden) contains the route transition's slide
        // without turning this into a scroll container, which would break
        // document-level scrolling and sticky headers on mobile.
        "bg-background relative z-20 min-h-screen w-full min-w-0 flex-1 overflow-visible overflow-x-clip md:h-full md:min-h-0 md:overflow-scroll"
      }
    >
      <RouteTransition>
        {(location) => (
          <Routes location={location}>
            {permissions.view_settings_page && (
              <Route
                path="/settings"
                element={withSuspense(<ClientSettings />)}
              ></Route>
            )}
            {permissions.view_users_page && (
              <Route path="/users" element={withSuspense(<Users />)}></Route>
            )}
            {isFeatureEnabled("waitlist") && permissions.view_waitlist && (
              <Route
                path="/waitlist"
                element={withSuspense(<Waitlist />)}
              ></Route>
            )}
            {canViewChatBooking({ isFeatureEnabled, permissions }) && (
              <Route
                path="/chat-booking"
                element={withSuspense(<ChatBooking />)}
              ></Route>
            )}
            {isFeatureEnabled("fixedAppointments") && (
              <Route
                path="/fixed-appointments"
                element={withSuspense(<FixedAppointments />)}
              ></Route>
            )}
            {dashboardSurfaces.showAnalytics && (
              <Route
                path="/analytics"
                element={withSuspense(<Analytics />)}
              ></Route>
            )}
            {dashboardSurfaces.showAnalytics && analyticsSurface === "v2" && (
              // Its own path, not a replacement: the flag adds a surface and
              // never takes the legacy one away. Splat so the new shell can own
              // its sub-routes without reopening this file.
              <Route
                path="/analytics/v2/*"
                element={withSuspense(<AnalyticsV2 />)}
              ></Route>
            )}
            {canAccessClientBilling(accountInfo) && (
              <Route
                path="/billing"
                element={withSuspense(<Billing />)}
              ></Route>
            )}
            <Route
              path="/community"
              element={withSuspense(<Community />)}
            ></Route>
            {permissions.view_locations_page && (
              <Route
                path="/locations"
                element={withSuspense(<Locations />)}
              ></Route>
            )}
            {isFeatureEnabled("marketplace") &&
              permissions.view_locations_page && (
                <>
                  <Route
                    path="/marketplace/settings"
                    element={withSuspense(
                      <MarketplaceSettings tab="locations" />,
                    )}
                  ></Route>
                  <Route
                    path="/marketplace/settings/fees"
                    element={withSuspense(<MarketplaceSettings tab="fees" />)}
                  />
                  <Route
                    path="/marketplace/setup"
                    element={withSuspense(<MarketplaceSetup />)}
                  >
                    <Route
                      path="location/:locationId"
                      element={withSuspense(<MarketplaceLocationSetup />)}
                    >
                      <Route
                        index
                        element={withSuspense(<MarketplaceLocationProfile />)}
                      />
                      <Route
                        path="images/guidelines"
                        element={withSuspense(
                          <MarketplaceImageGuidelinesStep />,
                        )}
                      />
                      <Route
                        path="images/upload"
                        element={withSuspense(<MarketplaceImageUploadStep />)}
                      />
                      <Route
                        path="location-picker"
                        element={withSuspense(
                          <MarketplaceLocationPickerStep />,
                        )}
                      />
                      <Route
                        path="description"
                        element={withSuspense(<MarketplaceDescriptionStep />)}
                      />
                      <Route
                        path="details"
                        element={withSuspense(<MarketplaceDetailsStep />)}
                      />
                      <Route
                        path="config"
                        element={withSuspense(<MarketplaceConfigStep />)}
                      />
                    </Route>
                  </Route>
                </>
              )}
            {permissions.view_services_page && (
              <>
                <Route
                  path="/services"
                  element={withSuspense(<ServicesPage />)}
                >
                  <Route index element={withSuspense(<Services />)} />
                  <Route
                    path="/services/categories"
                    element={withSuspense(<ServiceCategories />)}
                  />

                  {isFeatureEnabled("serviceAddons") && (
                    <>
                      <Route
                        path="/services/addons"
                        element={withSuspense(<ServiceAddons />)}
                      />
                      <Route
                        path="/services/addons/categories"
                        element={withSuspense(<ServiceAddonCategories />)}
                      />
                    </>
                  )}

                  <Route
                    path="/services/discounts"
                    element={withSuspense(<ServiceDiscounts />)}
                  />
                  <Route
                    path="/services/anchor-review"
                    element={withSuspense(<AnchorReview />)}
                  />
                </Route>

                {isFeatureEnabled("serviceAddons") && (
                  <>
                    <Route
                      path="/services/addons/:id"
                      element={withSuspense(<ManageServiceAddon />)}
                    />
                    <Route
                      path="/services/addons/categories/:id"
                      element={withSuspense(<ManageServiceAddonCategory />)}
                    />
                  </>
                )}

                <Route
                  path="/services/discounts/:id"
                  element={withSuspense(<ManageServiceDiscount />)}
                />

                <Route
                  path="/services/:id"
                  element={withSuspense(<ManageService />)}
                />
                <Route
                  path="/services/categories/:id"
                  element={withSuspense(<ManageCategory />)}
                />
              </>
            )}

            {isFeatureEnabled("discountCodes") &&
              permissions.view_discount_codes_page && (
                <>
                  <Route
                    path="/discount-codes"
                    element={withSuspense(<DiscountCodes />)}
                  />
                  <Route
                    path="/discount-codes/:id"
                    element={withSuspense(<ManageDiscountCode />)}
                  />
                </>
              )}

            {/* Grid or day-list, chosen by ?mode= — see useCalendarMode. */}
            {dashboardSurfaces.showCalendar && (
              <Route path="/calendar" element={<CalendarRoute />}></Route>
            )}
            {permissions.view_customer_list && (
              <>
                <Route
                  path="/customers"
                  element={withSuspense(<Customers />)}
                />
                <Route
                  path="/customers/:customerId"
                  element={withSuspense(<CustomerPage />)}
                />
              </>
            )}
            {permissions.view_availability && (
              <Route
                path="/schedule"
                element={withSuspense(<Schedule />)}
              ></Route>
            )}
            {permissions.view_notifications_page && (
              <>
                <Route
                  path="/notifications/*"
                  element={withSuspense(<NotificationSettings />)}
                ></Route>
                <Route
                  path="/notifications/settings/:id"
                  element={withSuspense(<ManageNotificationSetting />)}
                ></Route>
              </>
            )}
            {["admin", "salesman"].includes(accountInfo.systemRole) && (
              <Route
                path="/admin/*"
                element={withSuspense(<LimeAdmin />)}
              ></Route>
            )}
            {isFeatureEnabled("changeLog") &&
              permissions.view_change_log_page && (
                <Route
                  path="/change-log"
                  element={withSuspense(<ChangeLog />)}
                ></Route>
              )}
            {isFeatureEnabled("payments") && (
              <Route path="/stripe" element={withSuspense(<Stripe />)}>
                <Route index element={<Navigate to="general" replace />} />
                <Route
                  path="general"
                  element={withSuspense(<StripeGeneralTab />)}
                />
                <Route
                  path="payments"
                  element={withSuspense(<StripePaymentsOverviewTab />)}
                />
                <Route
                  path="settings"
                  element={withSuspense(<StripeAccountSettings />)}
                />
                {isFeatureEnabled("advancedPayments") && (
                  <Route
                    path="settings/services"
                    element={withSuspense(<StripeServicePaymentSettings />)}
                  />
                )}
                <Route path="status" element={withSuspense(<StripeStatus />)} />
              </Route>
            )}
            {isFeatureEnabled("resources") && (
              <>
                <Route
                  path="resource-groups"
                  element={withSuspense(<ResourceGroups />)}
                />
                <Route
                  path="resource-groups/:id"
                  element={withSuspense(<ManageResourceGroup />)}
                >
                  <Route index element={<Navigate to="general" replace />} />
                  <Route
                    path="general"
                    element={withSuspense(<ResourceGroupGeneralTab />)}
                  />
                  {!isFeatureEnabled(
                    "complexServiceResourceGroupRelations",
                  ) && (
                    <Route
                      path="services"
                      element={withSuspense(<ResourceGroupServicesTab />)}
                    />
                  )}
                  <Route
                    path="schedule"
                    element={withSuspense(<ResourceGroupScheduleTab />)}
                  />
                  <Route
                    path="resources/:resourceId"
                    element={withSuspense(<ManageResource />)}
                  />
                  <Route
                    path="resources"
                    element={withSuspense(<ResourceGroupResourcesTab />)}
                  />
                </Route>
              </>
            )}
            <>
              {hasAnyIntegration && permissions.view_integrations_page && (
                <Route
                  path="integrations"
                  element={withSuspense(<Integrations />)}
                >
                  <Route
                    index
                    element={withSuspense(<IntegrationsOverview />)}
                  />

                  {canAccessIntegration.fbcapi && (
                    <Route
                      path="fbcapi"
                      element={withSuspense(<FacebookConversionApi />)}
                    />
                  )}

                  {canAccessIntegration.api && (
                    <Route path="api/*" element={withSuspense(<ApiKeys />)}>
                      <Route
                        path=":id"
                        element={withSuspense(<ManageApiKey />)}
                      />
                    </Route>
                  )}

                  {canAccessIntegration.webhooks && (
                    <Route
                      path="webhooks/*"
                      element={withSuspense(<Webhooks />)}
                    >
                      <Route
                        path=":id"
                        element={withSuspense(<ManageWebhookEndpoint />)}
                      />
                    </Route>
                  )}

                  {canAccessIntegration.terminals && (
                    <>
                      <Route
                        path="terminals"
                        element={withSuspense(<PaymentTerminals />)}
                      />
                      <Route
                        path="terminals/sumup/callback"
                        element={withSuspense(<SumUpOAuthCallback />)}
                      />
                    </>
                  )}

                  <Route
                    path="*"
                    element={<Navigate to="/dashboard/integrations" replace />}
                  />
                </Route>
              )}
            </>

            {isFeatureEnabled("giftCard") && (
              <>
                <Route path="gift-card" element={withSuspense(<GiftCard />)} />
                <Route
                  path="gift-card/:id"
                  element={withSuspense(<GiftCardDetails />)}
                />
              </>
            )}

            {isFeatureEnabled("subscriptions") &&
              permissions.view_subscriptions_page && (
                <>
                  <Route
                    path="/subscriptions"
                    element={withSuspense(<Subscriptions />)}
                  >
                    <Route
                      path=""
                      element={withSuspense(<SubscriptionsTab />)}
                    />
                    <Route path="tags" element={withSuspense(<TagsTab />)} />
                    <Route
                      path="categories"
                      element={withSuspense(<CategoriesTab />)}
                    />
                    <Route
                      path="settings"
                      element={withSuspense(<SubscriptionSettingsTab />)}
                    />
                    {isFeatureEnabled("customerSubscriptionSelfFreeze") && (
                      <Route
                        path="freeze-requests"
                        element={
                          <Navigate
                            to="/dashboard/customer-subscriptions-overview/freeze-requests"
                            replace
                          />
                        }
                      />
                    )}
                  </Route>
                  <Route
                    path="/subscriptions/:subscriptionId/variations"
                    element={withSuspense(<SubscriptionVariations />)}
                  />
                </>
              )}
            {isFeatureEnabled("subscriptions") &&
              permissions.view_customer_subscriptions_overview_page && (
                <Route
                  path="/customer-subscriptions-overview"
                  element={withSuspense(
                    <CustomerSubscriptionsOverviewLayout />,
                  )}
                >
                  <Route
                    path=""
                    element={withSuspense(<CustomerSubscriptionsOverview />)}
                  />
                  {isFeatureEnabled("customerSubscriptionSelfFreeze") &&
                    permissions.manage_subscriptions && (
                      <Route
                        path="freeze-requests"
                        element={withSuspense(<FreezeRequestsTab />)}
                      />
                    )}
                </Route>
              )}

            {isFeatureEnabled("taxRegister") && (
              <>
                {permissions.view_invoice_archive_page && (
                  <Route
                    path="/tax-register/invoice/:invoiceId"
                    element={withSuspense(<TaxRegisterInvoices />)}
                  ></Route>
                )}
                {permissions.view_pos_page && (
                  <Route
                    path="/tax-register/invoice/new"
                    element={withSuspense(<POS />)}
                  ></Route>
                )}
                {permissions.view_invoice_archive_page && (
                  <Route
                    path="/tax-register/invoice"
                    element={withSuspense(<TaxRegisterInvoices />)}
                  ></Route>
                )}

                {(permissions.view_cash_register_status_summary_view ||
                  permissions.view_cash_register_closures ||
                  permissions.allow_closing_cash_register) && (
                  <Route
                    path="/tax-register/cash-register"
                    element={withSuspense(<CashRegister />)}
                  >
                    {permissions.view_cash_register_status_summary_view && (
                      <Route
                        path=":businessPremiseId/status"
                        element={withSuspense(<CashRegisterStatus />)}
                        index
                      />
                    )}
                    {(permissions.allow_closing_cash_register ||
                      permissions.view_cash_register_closures) && (
                      <Route
                        path=":businessPremiseId/closure"
                        element={withSuspense(<CashRegisterClosure />)}
                      />
                    )}
                  </Route>
                )}

                {permissions.view_tax_register_settings_page && (
                  <>
                    <Route
                      path="/tax-register/settings/setup"
                      element={withSuspense(<TaxRegisterSetup />)}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/info"
                      element={withSuspense(<TaxRegisterSetupBasicInfo />)}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/cert"
                      element={withSuspense(<TaxRegisterSetupCertificate />)}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/business-premise"
                      element={withSuspense(
                        <TaxRegisterSetupBusinessPremise />,
                      )}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/electronic-devices"
                      element={withSuspense(
                        <TaxRegisterSetupElectronicDevice />,
                      )}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/act"
                      element={withSuspense(<TaxRegisterSetupAct />)}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/printers"
                      element={withSuspense(<TaxRegisterSetupPrinters />)}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/bluetooth-printer"
                      element={
                        <Navigate
                          to="/dashboard/tax-register/settings/setup/receipt-printer"
                          replace
                        />
                      }
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/receipt-printer"
                      element={withSuspense(<TaxRegisterSetupReceiptPrinter />)}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/cash-reg-settings"
                      element={withSuspense(
                        <TaxRegisterSetupCashRegisterSettings />,
                      )}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/automatic-invoice-issuing"
                      element={withSuspense(
                        <TaxRegisterSetupAutomaticInvoiceIssuing />,
                      )}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/receipt-settings"
                      element={withSuspense(<TaxRegisterReceiptSettings />)}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/document-settings"
                      element={withSuspense(<TaxRegisterDocumentSettings />)}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/accounting-settings"
                      element={withSuspense(
                        <TaxRegisterSetupAccountingSettings />,
                      )}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/email-address"
                      element={withSuspense(<TaxRegisterSetupEmailAddress />)}
                    ></Route>
                    <Route
                      path="/tax-register/settings/setup/pos-settings"
                      element={withSuspense(<TaxRegisterSetupPOSSettings />)}
                    ></Route>
                  </>
                )}

                {isFeatureEnabled("products") &&
                  permissions.view_products_page && (
                    <>
                      <Route
                        path="/products"
                        element={withSuspense(<Products tab="products" />)}
                      ></Route>
                      <Route
                        path="/products/categories"
                        element={withSuspense(<Products tab="categories" />)}
                      ></Route>

                      <Route
                        path="/products/categories/:categoryId"
                        element={withSuspense(<ManageProductCategory />)}
                      ></Route>
                      <Route
                        path="/products/:productId"
                        element={withSuspense(<ManageProduct />)}
                      ></Route>
                    </>
                  )}

                {isFeatureEnabled("products") &&
                  permissions.view_stock_taking_page && (
                    <>
                      <Route
                        path="/stock-taking"
                        element={withSuspense(<StockTaking />)}
                      ></Route>
                      <Route
                        path="/stock-taking/:stockTakingSessionId"
                        element={withSuspense(<StockTakingSession />)}
                      ></Route>
                    </>
                  )}

                {isFeatureEnabled("products") &&
                  permissions.view_delivery_orders_page && (
                    <>
                      <Route
                        path="/delivery-orders"
                        element={withSuspense(<DeliveryOrders />)}
                      ></Route>
                      <Route
                        path="/delivery-orders/:deliveryOrderId"
                        element={withSuspense(<ManageDeliveryOrder />)}
                      ></Route>
                    </>
                  )}

                {isFeatureEnabled("products") &&
                  permissions.view_product_stock_page && (
                    <>
                      <Route
                        path="/products/stock"
                        element={withSuspense(<ProductStock />)}
                      ></Route>
                      <Route
                        path="/products/internal-use/create"
                        element={withSuspense(<CreateProductInternalUse />)}
                      ></Route>
                      <Route
                        path="/products/write-off/create"
                        element={withSuspense(<CreateProductWriteOff />)}
                      ></Route>
                    </>
                  )}
              </>
            )}

            <Route
              path={"*"}
              element={<Navigate to={dashboardSurfaces.landingLink} />}
            />
          </Routes>
        )}
      </RouteTransition>
    </div>
  );
};
